'use client';

import { useEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { api, useQuery } from '@/lib/convex';
import { getChannelNotificationCopy } from '@/i18n/channel-notification-copy';
import { getConnectNotifyCopy } from '@/i18n/connect-notify-copy';
import {
  activityBaseline,
  activityToastText,
  diffActivitySignals,
  type ActivityBaseline,
} from './mention-toast-state';
import {
  useWorkChatsNavigation,
  useWorkChatsRoute,
} from './use-work-chats-navigation';

function firstParam(value: string | string[] | undefined): string {
  if (typeof value === 'string') return value;
  return Array.isArray(value) && typeof value[0] === 'string' ? value[0] : '';
}

/**
 * App-wide Connect toasts (mounted in the workspace layout, so mentions and
 * DMs surface on every page, not only under /chats). Diffs per-chat counters
 * after the first snapshot — historical activity never toasts — honoring the
 * viewer's notification mode and mute, and skipping the chat that is open.
 * The line names the person who wrote, and "Open" jumps to the chat.
 */
export function MentionToastWatcher(): null {
  const t = useTranslations('WorkChats');
  const locale = useLocale();
  const navigation = useWorkChatsNavigation();
  const params = useParams<{ orgSlug?: string }>();
  // The open chat comes from the pathname: in-place chat switches leave the
  // matched `[channelId]` param stale.
  const route = useWorkChatsRoute();
  const orgSlug = firstParam(params?.orgSlug);
  const activeChannelId = route?.kind === 'channel' ? route.channelId : '';

  const { data: signals } = useQuery(
    api.organizations.channels.activity_signals.listActivitySignals,
    orgSlug ? { orgSlug } : 'skip'
  );

  const baselineRef = useRef<{
    orgSlug: string;
    counts: ActivityBaseline;
  } | null>(null);
  const activeChannelRef = useRef(activeChannelId);
  useEffect(() => {
    activeChannelRef.current = activeChannelId;
  }, [activeChannelId]);

  useEffect(() => {
    if (!signals) return;
    const next = activityBaseline(signals);
    const baseline = baselineRef.current;
    baselineRef.current = { orgSlug, counts: next };
    // First snapshot (or a workspace switch): seed, never toast history.
    if (baseline === null || baseline.orgSlug !== orgSlug) return;

    const notify = getConnectNotifyCopy(locale);
    const legacy = getChannelNotificationCopy(locale);
    const copy = {
      ...notify,
      newMessage: legacy.message,
      fallbackChat: t('directMessagesSection'),
    };
    for (const item of diffActivitySignals(baseline.counts, signals, {
      activeChannelId: activeChannelRef.current,
      now: Date.now(),
    })) {
      toast(activityToastText(item, copy), {
        action: {
          label: notify.open,
          onClick: () => navigation.push(`/${orgSlug}/chats/${item.channelId}`),
        },
      });
    }
  }, [signals, locale, navigation, orgSlug, t]);

  return null;
}
